import React from 'react'
import { PendingAction, SYNTHETIC_PENDING_ACTIONS } from './syntheticDashboardData'
import { Calendar, MessageSquare, Clock, ArrowRight } from 'lucide-react'

interface PendingActionsListProps {
  actions?: PendingAction[]
  onSelectAction?: (action: PendingAction) => void
  compact?: boolean
}

export const PendingActionsList: React.FC<PendingActionsListProps> = ({
  actions = SYNTHETIC_PENDING_ACTIONS,
  onSelectAction,
  compact = false,
}) => {
  const getIcon = (type: PendingAction['type']) => {
    switch (type) {
      case 'contact_request':
        return <MessageSquare size={14} color="#b91c1c" />
      case 'availability_review':
        return <Calendar size={14} color="#b45309" />
      case 'form':
        return <Clock size={14} color="#1d4ed8" />
    }
  }

  const getBadgeStyle = (severity: PendingAction['severity']) => {
    switch (severity) {
      case 'high':
        return { bg: '#fee2e2', text: '#b91c1c', border: '#fecaca', label: 'Urgente' }
      case 'medium':
        return { bg: '#fef3c7', text: '#b45309', border: '#fde68a', label: 'Atención' }
      case 'low':
        return { bg: '#e0f2fe', text: '#0284c7', border: '#bae6fd', label: 'Borrador' }
    }
  }

  return (
    <div className="pending-actions-card" style={{
      background: 'var(--surface-card, #ffffff)',
      border: '1px solid var(--surface-border, #e2e8f0)',
      borderRadius: '8px',
      padding: compact ? '1rem' : '1.25rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.85rem',
      }}>
        <div>
          <div style={{
            fontSize: compact ? '0.75rem' : '0.8rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted, #64748b)',
          }}>
            Acciones Operativas Requeridas
          </div>
          <div style={{
            fontSize: compact ? '1.1rem' : '1.25rem',
            fontWeight: 800,
            color: 'var(--text-main, #0f172a)',
            fontVariantNumeric: 'tabular-nums',
            marginTop: '2px',
          }}>
            {actions.length} pendientes de resolución
          </div>
        </div>

        <span style={{
          background: 'var(--brand-red, #b91c1c)',
          color: '#ffffff',
          borderRadius: '999px',
          padding: '2px 8px',
          fontSize: '0.72rem',
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {actions.filter((a) => a.severity === 'high').length} urgentes
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {actions.map((act) => {
          const badge = getBadgeStyle(act.severity)
          return (
            <div
              key={act.id}
              onClick={() => onSelectAction && onSelectAction(act)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                padding: compact ? '8px 10px' : '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--surface-border, #e2e8f0)',
                background: act.severity === 'high' ? 'rgba(254, 226, 226, 0.25)' : 'var(--surface-subtle, #f8fafc)',
                cursor: onSelectAction ? 'pointer' : 'default',
                transition: 'border-color 0.15s ease, background 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <div style={{
                  padding: '6px',
                  borderRadius: '6px',
                  background: '#ffffff',
                  border: '1px solid var(--surface-border, #e2e8f0)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '1px',
                }}>
                  {getIcon(act.type)}
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      color: 'var(--text-main, #0f172a)',
                    }}>
                      {act.title}
                    </span>
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      background: badge.bg,
                      color: badge.text,
                      border: `1px solid ${badge.border}`,
                    }}>
                      {badge.label}
                    </span>
                  </div>

                  <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary, #475569)',
                    marginTop: '2px',
                    lineHeight: 1.35,
                  }}>
                    {act.subtitle}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '4px',
                    fontSize: '0.7rem',
                    color: 'var(--text-muted, #64748b)',
                  }}>
                    <strong style={{ color: 'var(--text-main, #0f172a)' }}>
                      #{act.athleteDorsal} {act.athleteName}
                    </strong>
                    <span>·</span>
                    <span>{act.date}</span>
                  </div>
                </div>
              </div>

              {onSelectAction && (
                <div style={{
                  color: 'var(--text-muted, #64748b)',
                  paddingTop: '4px',
                }}>
                  <ArrowRight size={14} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
